import { useState, useEffect, useCallback, useContext } from 'react';
import FrontendHttpClient from '@/utils/http-handler/frontend-http-client';
import NotificationService from '@/components/notification-service';
import { AppContext } from '@/utils/app-context';
import reducerList from '@/utils/reducer-list';
import { useStore } from './use-store';
import { useBulkJob } from './use-bulk-job';
import { useBulkJobId } from './use-bulk-job-id';

/**
 * Hook to automatically switch to the correct insured when accessing a bulk job
 * that belongs to a different insured than the one currently active.
 *
 * @returns {{ switchingInsured: boolean }} - Returns true while switching insureds
 */
export const useBulkJobInsuredSwitch = () => {
  const { data: bulkJob } = useBulkJob();
  const bulkJobId = useBulkJobId();
  const [{ user }] = useStore();
  const dispatch = useContext(AppContext)[1];
  const [switchingInsured, setSwitchingInsured] = useState(false);

  const switchInsuredIfNeeded = useCallback(async () => {
    if (!bulkJobId || bulkJob || switchingInsured) {
      return;
    }

    try {
      const { data } = await FrontendHttpClient.get(
        `/api/v2/bulk_jobs/${bulkJobId}/insured`
      );

      if (data?.insuredId && data.insuredId !== user.insuredId) {
        setSwitchingInsured(true);

        FrontendHttpClient.post('/api/v2/policies/ams/pre-fetch', {
          insuredId: data.insuredId,
        });

        const response = await FrontendHttpClient.put('/api/v2/users/insured', {
          insuredId: data.insuredId,
        });

        dispatch({
          type: reducerList.SET_USER,
          payload: response.data.record,
        });
      }
    } catch (err) {
      NotificationService.error('Error switching insured');
      console.error('Error switching insured:', err);
      setSwitchingInsured(false);
    } finally {
      setSwitchingInsured(false);
    }
  }, [bulkJobId, bulkJob, user.insuredId, dispatch, switchingInsured]);

  useEffect(() => {
    switchInsuredIfNeeded();
  }, [switchInsuredIfNeeded]);

  return { switchingInsured };
};
